import {css, cx as addStyles} from '@emotion/css';
import React, {ButtonHTMLAttributes, HTMLAttributes, StyleHTMLAttributes, useState} from 'react';
import {theme} from './Common';
import {ArrowDownIcon, ArrowUpIcon} from './Icons';

export type OptionProps<T> = {value: T; label: string; disabled?: boolean};
export type SelectProps<T> = {
  options?: OptionProps<T>[];
  isLarge?: boolean;
  disabled?: boolean;
  value: T;
  placeholder?: string;
  onChange: (value: T) => unknown;
  containerClassName?: string;
  containerStyle?: StyleHTMLAttributes<HTMLDivElement>;
};
export const Select = <T,>({
  options = [],
  disabled = false,
  isLarge = false,
  value,
  placeholder,
  onChange,
  containerClassName,
  containerStyle,
}: SelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleSelect = () => setIsOpen(isOpen => !isOpen);
  const optionSelect = (option: T) => {
    onChange(option);
    setIsOpen(false);
  };
  return (
    <Wrapper isLarge={isLarge} className={containerClassName} style={containerStyle}>
      <SelectContainer disabled={disabled} isLarge={isLarge} isOpen={isOpen} onClick={toggleSelect}>
        <Selects disabled={disabled}>
          {value ? options.find(option => value === option.value)?.label : placeholder || 'Select'}
        </Selects>
        {isOpen ? <ArrowUpIcon size='small' /> : <ArrowDownIcon size='small' />}
      </SelectContainer>
      {isOpen && (
        <OptionsContainer disabled={disabled}>
          {options.map(({label, disabled, value}) => (
            <Option key={label} disabled={disabled} onClick={() => optionSelect(value)}>
              {label}
            </Option>
          ))}
        </OptionsContainer>
      )}
    </Wrapper>
  );
};
const Wrapper = ({
  isLarge,
  className,
  ...rest
}: {isLarge?: boolean} & HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        height: '49px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: isLarge ? '100%' : '130px',
      }),
      className,
    )}
    {...rest}
  />
);
const disabledContainerStyle = css({opacity: 0.5, cursor: 'not-allowed'});
const openedContainerStyle = css({
  border: `1px solid ${theme.colors.darkBlue}`,
  borderBottom: 'none',
});
const closedContainerStyle = css({
  border: `1px solid ${theme.colors.darkBlue}`,
  ':hover': {border: `2px solid ${theme.colors.darkBlue}`},
});
type SelectContainerProps = {
  isLarge?: boolean;
  isOpen?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;
const SelectContainer = ({isLarge, isOpen, disabled, ...rest}: SelectContainerProps) => (
  <button
    className={addStyles(
      css({
        position: 'relative',
        cursor: 'pointer',
        width: isLarge ? '100%' : '130px',
        height: 'inherit',
        padding: `0 ${theme.spacings.xxsmall}`,
        transition: 'border 0.1s ease-in-out',
        lineHeight: 1,
        background: theme.colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        outline: 'none',
      }),
      {[openedContainerStyle]: isOpen},
      {[closedContainerStyle]: !isOpen},
    )}
    {...rest}
  />
);
const Selects = ({disabled, ...rest}: {disabled?: boolean} & HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        width: '100%',
        border: '0',
        outline: 'none',
        color: theme.colors.darkBlue,
        fontSize: theme.font.sizes.small,
        cursor: 'pointer',
        background: theme.colors.white,
        textAlign: 'left',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
      }),
      {[disabledContainerStyle]: disabled},
    )}
    {...rest}
  />
);
const OptionsContainer = ({
  disabled,
  ...rest
}: {disabled?: boolean} & HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        width: '100%',
        height: '110px',
        borderBottom: `1px solid ${theme.colors.darkBlue}`,
        borderLeft: `1px solid ${theme.colors.darkBlue}`,
        borderRight: `1px solid ${theme.colors.darkBlue}`,
        transition: 'border 0.1s ease-in-out',
        lineHeight: '1',
        background: theme.colors.white,
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        position: 'absolute',
        top: '49px',
        zIndex: 99,
        overflowY: 'scroll',
      }),
      {[disabledContainerStyle]: disabled},
    )}
    {...rest}
  />
);
const Option = ({disabled, ...rest}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={css({
      outline: 'none',
      border: 0,
      width: '100%',
      height: '49px',
      padding: theme.spacings.xxsmall,
      textAlign: 'left',
      fontSize: theme.font.sizes.small,
      color: disabled ? '#2E319225' : theme.colors.darkBlue,
      cursor: disabled ? 'not-allowed !important' : 'pointer',
      backgroundColor: disabled ? '#66666625' : theme.colors.white,
      ':hover': {backgroundColor: disabled ? 'none' : '#F7941D25'},
    })}
    disabled={disabled}
    {...rest}
  />
);
